import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Drawer,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';
import { History as HistoryIcon, Close as CloseIcon } from '@material-ui/icons';
import { format } from 'date-fns';
import {
  twilioMessageHistoryQuery,
  TwilioMessageHistoryData,
  TwilioMessageHistoryVars,
} from '../../../graph/twilioMessages';
import { useDebugMode } from '../../../hooks/useDebugMode';

interface Props {
  phone: string;
  label?: string;
}

/**
 * Side panel that shows SMS message history from Twilio.
 * Only visible when canBypassPermissions is true (SuperAdmin + debug mode).
 */
const MessageHistorySidePanel: React.FC<Props> = ({ phone, label = 'Message History' }) => {
  const { canBypassPermissions } = useDebugMode();
  const [open, setOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery<TwilioMessageHistoryData, TwilioMessageHistoryVars>(
    twilioMessageHistoryQuery,
    {
      variables: { phone, limit: 20 },
      skip: !open,
      fetchPolicy: 'network-only',
    }
  );

  if (!canBypassPermissions || !phone) return null;

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus === 'delivered' || lowerStatus === 'sent') return 'primary';
    if (lowerStatus === 'failed' || lowerStatus === 'undelivered') return 'secondary';
    return 'default';
  };

  const handleOpen = () => {
    setOpen(true);
    if (data) refetch();
  };

  return (
    <>
      <Tooltip title={`View ${label}`}>
        <IconButton size="small" onClick={handleOpen} style={{ marginLeft: 4, padding: 2 }}>
          <HistoryIcon fontSize="small" style={{ fontSize: 16, opacity: 0.6 }} />
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box width={550} p={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{label}</Typography>
            <IconButton onClick={() => setOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography variant="body2" color="textSecondary" gutterBottom>
            Phone: {phone}
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error">Error: {error.message}</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Direction</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.twilioMessageHistory || []).map(msg => (
                  <TableRow key={msg.sid}>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {format(new Date(msg.sentAt), 'MM/dd HH:mm')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={msg.direction === 'outbound-api' ? 'Sent' : 'Received'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={msg.status} size="small" color={getStatusColor(msg.status)} />
                    </TableCell>
                    <TableCell
                      style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      <Tooltip title={msg.body || ''}>
                        <span>
                          {msg.body?.substring(0, 50)}
                          {msg.body && msg.body.length > 50 ? '...' : ''}
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!loading && !error && (data?.twilioMessageHistory || []).length === 0 && (
            <Typography color="textSecondary" align="center" style={{ marginTop: 32 }}>
              No messages found
            </Typography>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default MessageHistorySidePanel;
